import { useEffect, useRef, useState } from "react";

// A recording remains local until Send. Leaving the composer always releases the mic.
export default function useVoiceRecording({ onComplete, onStart, onError }) {
	const callbacks = useRef({ onComplete, onStart, onError });
	callbacks.current = { onComplete, onStart, onError };
	const session = useRef(null);
	const generation = useRef(0);
	const [isRecording, setIsRecording] = useState(false);
	const [isPaused, setIsPaused] = useState(false);
	const [isPending, setIsPending] = useState(false);
	const [duration, setDuration] = useState(0);
	const [analyser, setAnalyser] = useState(null);
	const [previewStream, setPreviewStream] = useState(null);

	const release = (current) => {
		clearInterval(current?.timer);
		current?.stream?.getTracks().forEach((track) => track.stop());
		current?.destination?.stream.getTracks().forEach((track) => track.stop());
		current?.context?.close().catch(() => {});
	};

	useEffect(() => () => {
		generation.current += 1;
		const current = session.current;
		session.current = null;
		if (current?.recorder && current.recorder.state !== "inactive") {
			current.recorder.onstop = null;
			current.recorder.stop();
		}
		release(current);
	}, []);

	const elapsed = (current) => current.elapsed + (current.startedAt == null ? 0 : performance.now() - current.startedAt);

	const start = async (mode = "audio") => {
		if (session.current) return;
		const current = { chunks: [], elapsed: 0, startedAt: null, generation: ++generation.current };
		session.current = current;
		setIsPending(true);
		try {
			if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
				throw new Error("Your browser does not support audio recording.");
			}
			// Resume Web Audio during the tap, before the microphone permission prompt.
			current.context = new (window.AudioContext || window.webkitAudioContext)();
			await current.context.resume();
			if (generation.current !== current.generation) { release(current); return; }
			current.stream = await navigator.mediaDevices.getUserMedia({ video: mode === "video" ? { facingMode: "user", width: { ideal: 480 }, height: { ideal: 480 } } : false, audio: {
				channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true
			} });
			if (generation.current !== current.generation) { release(current); return; }
			const source = current.context.createMediaStreamSource(current.stream);
			const filter = current.context.createBiquadFilter();
			filter.type = "highpass";
			filter.frequency.value = 80;
			const meter = current.context.createAnalyser();
			meter.fftSize = 1024;
			current.destination = current.context.createMediaStreamDestination();
			source.connect(filter);
			filter.connect(meter);
			meter.connect(current.destination);
			if (mode === "video") current.stream.getVideoTracks().forEach(track => current.destination.stream.addTrack(track));
			const mimeType = (mode === "video" ? ["video/mp4", "video/webm;codecs=vp8,opus", "video/webm"] : ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"])
				.find((type) => MediaRecorder.isTypeSupported(type));
			const recorder = new MediaRecorder(current.destination.stream, {
				...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 192000
			});
			current.recorder = recorder;
			recorder.ondataavailable = ({ data }) => { if (data?.size) current.chunks.push(data); };
			recorder.onstop = () => {
				if (current.finished) return;
				current.finished = true;
				release(current);
				if (generation.current !== current.generation) return;
				session.current = null;
				setIsRecording(false);
				setIsPaused(false);
				setIsPending(false);
				setAnalyser(null);
				setPreviewStream(null);
				if (current.discard || !current.chunks.length) return;
				const type = recorder.mimeType || mimeType || "audio/webm";
				const extension = type.includes("mp4") ? (mode === "video" ? "mp4" : "m4a") : type.includes("ogg") ? "ogg" : "webm";
				const file = new File(current.chunks, `${mode === "video" ? "video_note" : "recording"}_${Date.now()}.${extension}`, { type });
				callbacks.current.onComplete?.({ file, chunks: current.chunks, duration: current.elapsed / 1000, send: current.send });
			};
			recorder.onerror = (event) => {
				current.discard = true;
				if (recorder.state === "inactive") recorder.onstop();
				else stop();
				callbacks.current.onError?.(event.error || new Error("Could not record audio."));
			};
			recorder.start(250);
			current.startedAt = performance.now();
			current.timer = setInterval(() => {
                const seconds = elapsed(current) / 1000;
                setDuration(seconds);
                if (mode === "video" && seconds >= 60) stop(true);
            }, 100);
			setDuration(0);
			setAnalyser(meter);
			setPreviewStream(mode === "video" ? current.stream : null);
			setIsPaused(false);
			setIsRecording(true);
			setIsPending(false);
			callbacks.current.onStart?.();
			if (current.finishRequested) stop(current.send);
		} catch (error) {
			release(current);
			if (generation.current !== current.generation) return;
			session.current = null;
			setAnalyser(null);
			setPreviewStream(null);
			setIsRecording(false);
			setIsPending(false);
			callbacks.current.onError?.(error);
		}
	};

	const stop = (send = false) => {
		const current = session.current;
		if (!current) return;
		if (!current.recorder) { current.finishRequested = true; current.send = send === true; return; }
		if (current.recorder.state === "inactive") return;
		current.elapsed = elapsed(current);
		current.startedAt = null;
		current.send = send === true;
		clearInterval(current.timer);
		setIsPending(true);
		current.recorder.stop();
	};

	const discard = () => {
		if (!session.current) return;
		session.current.discard = true;
		stop();
	};

	const togglePause = () => {
		const current = session.current;
		if (!current?.recorder) return;
		if (current.recorder.state === "recording") {
			current.recorder.pause();
			current.elapsed = elapsed(current);
			current.startedAt = null;
			setDuration(current.elapsed / 1000);
			setIsPaused(true);
		} else if (current.recorder.state === "paused") {
			current.recorder.resume();
			current.startedAt = performance.now();
			setIsPaused(false);
		}
	};

	return { isRecording, isPaused, isPending, duration, analyser, previewStream, start, stop, discard, togglePause };
}
